import React, { useState, useEffect } from 'react';
import {
  FiSend,
  FiCopy,
  FiCheck,
  FiX,
  FiClock,
  FiMail,
  FiRefreshCw,
  FiTrash2
} from 'react-icons/fi';
import { useToast } from '@/hooks/use-toast';
import { Dialog, DialogContent } from '@/components/mira';
import apiClient from '../../services/apiClient';
import Loader from 'wrapper/loader';

const CompanyInvitations = () => {
  const { toast } = useToast();

  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [resendingId, setResendingId] = useState(null);
  const [formData, setFormData] = useState({
    companyName: '',
    companyNmlsId: '',
    contactName: '',
    contactEmail: '',
    subdomain: '',
    message: '',
    plan: 'professional',
    country: 'US',
    region: 'US'
  });

  useEffect(() => {
    fetchInvitations();
  }, []);

  const fetchInvitations = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/platform/clients/invitations');
      const rawInvitations = Array.isArray(response.data)
        ? response.data
        : response.data?.invitations ?? [];

      const transformedInvitations = rawInvitations.map(inv => ({
        id: inv.id,
        companyName: inv.organization_name || '',
        contactName: [inv.first_name, inv.last_name].filter(Boolean).join(' '),
        contactEmail: inv.email,
        subdomain: inv.organization_subdomain || '',
        status: (inv.status || 'pending').toLowerCase(),
        sentAt: inv.created_at,
        expiresAt: inv.expires_at,
        acceptedAt: inv.accepted_at,
        invitationUrl: inv.invitation_url || `https://miralabs.ai/invite/${inv.invitation_code}`,
        emailSent: inv.email_sent ?? false
      }));
      setInvitations(transformedInvitations);
    } catch (error) {
      console.error('Error fetching invitations:', error);
      // Fallback to mock data if API fails
      setInvitations(getMockInvitations());
    } finally {
      setLoading(false);
    }
  };

  const getMockInvitations = () => [
    {
      id: 1,
      companyName: 'Regional Bank Corp',
      contactName: 'Michael Johnson',
      contactEmail: 'mjohnson@regionalbank.com',
      subdomain: 'regional-bank',
      status: 'pending',
      sentAt: '2024-03-15T10:30:00Z',
      expiresAt: '2024-03-22T10:30:00Z',
      invitationUrl: 'https://miralabs.ai/invite/abc123def456'
    },
    {
      id: 2,
      companyName: 'City Mortgage Partners',
      contactName: 'Emily Davis',
      contactEmail: 'emily@citymortgage.com',
      subdomain: 'city-mortgage',
      status: 'accepted',
      sentAt: '2024-03-10T14:00:00Z',
      acceptedAt: '2024-03-11T09:15:00Z'
    },
    {
      id: 3,
      companyName: 'Premier Lending Solutions',
      contactName: 'David Wilson',
      contactEmail: 'dwilson@premierlending.com',
      subdomain: 'premier-lending',
      status: 'expired',
      sentAt: '2024-03-01T12:00:00Z',
      expiresAt: '2024-03-08T12:00:00Z'
    }
  ];

  const handleSendInvitation = async () => {
    // Prevent multiple submissions
    if (submitting) return;

    // Validate NMLS ID: must be 6-12 digits
    if (!formData.companyNmlsId || formData.companyNmlsId.length < 6 || formData.companyNmlsId.length > 12) {
      toast({ title: 'Company NMLS ID must be between 6 and 12 digits.', variant: 'destructive' });
      return;
    }

    setSubmitting(true);
    let organizationCreated = false;
    let orgId = null;

    try {
      // Split contact name into first and last
      const [firstName, ...lastNameParts] = formData.contactName.split(' ');
      const lastName = lastNameParts.join(' ') || firstName;

      // Prepare the organization creation payload
      const orgPayload = {
        name: formData.companyName,
        subdomain: formData.subdomain,
        country: 'US',
        region: 'US',
        company_nmls_id: formData.companyNmlsId,
        admin_email: formData.contactEmail,
        admin_first_name: firstName,
        admin_last_name: lastName,
        subscription_plan: formData.plan
      };

      // Log the payload for debugging
      console.log('Creating organization with payload:', orgPayload);

      // First create the organization
      const orgResponse = await apiClient.post('/platform/clients/organizations', orgPayload);

      // Backend returns OrganizationResponse directly in data
      // Check for both possible response structures
      if (orgResponse.data.organization || orgResponse.data.organization_id) {
        organizationCreated = true;
        orgId = orgResponse.data.organization?.id || orgResponse.data.organization_id;

        // The backend automatically sends an invitation during organization creation,
        // so we don't need a separate invitation API call
        toast({
          title: `Organization "${formData.companyName}" created and invitation sent to ${formData.contactEmail}!`
        });

        // Close modal and reset form since organization was created successfully
        setShowInviteModal(false);
        setFormData({
          companyName: '',
          companyNmlsId: '',
          contactName: '',
          contactEmail: '',
          subdomain: '',
          message: '',
          plan: 'professional',
          country: 'US',
          region: 'US'
        });

        // Refresh the invitations list to show the new organization
        await fetchInvitations();

        // Also refresh the dashboard statistics
        // This will trigger a re-render with updated counts
        setTimeout(() => {
          fetchInvitations();
        }, 1000);
      }
    } catch (error) {
      console.error('Error creating organization:', error);

      // Check if it's a duplicate organization error
      if (error.response?.status === 409 || error.response?.data?.detail?.includes('already exists')) {
        toast({ title: `An organization with subdomain "${formData.subdomain}" already exists. Please use a different subdomain.`, variant: "destructive" });
      } else {
        const errorMessage = error.response?.data?.detail ||
          error.response?.data?.message ||
          'Failed to create organization. Please try again.';
        toast({ title: errorMessage, variant: "destructive" });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleResendInvitation = async (invitationId) => {
    setResendingId(invitationId);
    try {
      const response = await apiClient.post(`/platform/clients/invitations/${invitationId}/resend`);
      const message = response.data?.message || 'Invitation email sent successfully!';
      toast({ title: message });

      // Refresh the list to update the email status
      await fetchInvitations();
    } catch (error) {
      console.error('Error resending invitation:', error);
      const errorMessage = error.response?.data?.detail ||
        error.response?.data?.message ||
        'Failed to resend invitation';
      toast({ title: errorMessage, variant: "destructive" });
    } finally {
      setResendingId(null);
    }
  };

  const handleCopyLink = (url) => {
    navigator.clipboard.writeText(url);
    toast({ title: 'Invitation link copied to clipboard!' });
  };

  const handleDeleteInvitation = async (invitationId) => {
    if (window.confirm('Are you sure you want to revoke this invitation?')) {
      try {
        await apiClient.delete(`/platform/clients/invitations/${invitationId}`);
        setInvitations(invitations.filter(inv => inv.id !== invitationId));
        toast({ title: 'Invitation revoked successfully!' });
      } catch (error) {
        console.error('Error revoking invitation:', error);
        toast({ title: 'Failed to revoke invitation', variant: "destructive" });
      }
    }
  };

  const StatusBadge = ({ status }) => {
    const styles = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: FiClock },
      accepted: { bg: 'bg-green-100', text: 'text-green-800', icon: FiCheck },
      expired: { bg: 'bg-red-100', text: 'text-red-800', icon: FiX },
      declined: { bg: 'bg-gray-100', text: 'text-gray-800', icon: FiX }
    };

    const style = styles[status] || styles.pending;
    const Icon = style.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${style.bg} ${style.text}`}>
        <Icon className="mr-1" size={12} />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const InviteModal = () => (
    <Dialog
      show={showInviteModal}
      onOpenChange={(open) => {
        if (!open) setShowInviteModal(false);
      }}
      showClose={false}
    >
      <DialogContent
        showClose={false}
        className="max-w-2xl w-full rounded-2xl bg-white p-0 relative"
      >
        {/* Loading overlay */}
        {submitting && (
          <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10 rounded-lg">
            <Loader FullScreen={false} variant="colorful" message="Creating organization..." />
          </div>
        )}

        <div className="p-6 border-b">
          <h2 className="text-2xl font-bold text-gray-900">Invite New Company</h2>
          <p className="text-gray-600 mt-1">Send an invitation to join the MIRA platform</p>
        </div>

        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Company Name *
              </label>
              <input
                type="text"
                value={formData.companyName}
                onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary disabled:bg-gray-100 disabled:cursor-not-allowed"
                placeholder="Enter company name"
                disabled={submitting}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Subdomain *
              </label>
              <div className="flex">
                <input
                  type="text"
                  value={formData.subdomain}
                  onChange={(e) => setFormData({ ...formData, subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                  className="flex-1 px-3 py-2 border rounded-l-lg focus:outline-none focus:ring-2 focus:ring-primary disabled:bg-gray-100 disabled:cursor-not-allowed"
                  placeholder="company-name"
                  disabled={submitting}
                />
                <span className="px-3 py-2 bg-gray-100 border border-l-0 rounded-r-lg text-gray-600">
                  .{import.meta.env.VITE_APP_BASE_DOMAIN || 'miralabs.ai'}
                </span>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Company NMLS ID *
            </label>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={formData.companyNmlsId}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, '');
                setFormData({ ...formData, companyNmlsId: val });
              }}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary disabled:bg-gray-100 disabled:cursor-not-allowed"
              placeholder="Enter 6-12 digit NMLS ID"
              maxLength={12}
              disabled={submitting}
            />
            {formData.companyNmlsId && (formData.companyNmlsId.length < 6 || formData.companyNmlsId.length > 12) && (
              <p className="mt-1 text-xs text-red-600">NMLS ID must be between 6 and 12 digits</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Contact Name *
              </label>
              <input
                type="text"
                value={formData.contactName}
                onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary disabled:bg-gray-100 disabled:cursor-not-allowed"
                placeholder="Enter contact name"
                disabled={submitting}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Contact Email *
              </label>
              <input
                type="email"
                value={formData.contactEmail}
                onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary disabled:bg-gray-100 disabled:cursor-not-allowed"
                placeholder="email@company.com"
                disabled={submitting}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Country
              </label>
              <input
                type="text"
                value="United States (US)"
                readOnly
                className="w-full px-3 py-2 border rounded-lg bg-gray-100 text-gray-600"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Plan
              </label>
              <select
                value={formData.plan}
                onChange={(e) => setFormData({ ...formData, plan: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary disabled:bg-gray-100 disabled:cursor-not-allowed"
                disabled={submitting}
              >
                <option value="starter">Starter - Up to 5 loan officers</option>
                <option value="professional">Professional - Up to 20 loan officers</option>
                <option value="enterprise">Enterprise - Unlimited loan officers</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Personal Message (Optional)
            </label>
            <textarea
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary disabled:bg-gray-100 disabled:cursor-not-allowed"
              rows="3"
              placeholder="Add a personal message to the invitation..."
              disabled={submitting}
            />
          </div>
        </div>

        <div className="p-6 border-t flex justify-end space-x-3">
          <button
            onClick={() => setShowInviteModal(false)}
            className="px-4 py-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            onClick={handleSendInvitation}
            disabled={submitting || !formData.companyName || !formData.companyNmlsId || formData.companyNmlsId.length < 6 || !formData.contactEmail || !formData.contactName || !formData.subdomain}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {submitting ? (
              <Loader variant="button" message="Sending..." />
            ) : (
              <>
                <FiSend className="mr-2" size={16} />
                Send Invitation
              </>
            )}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );

  if (loading) {
    return <Loader message="Loading invitations..." />;
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">Company Invitations</h2>
          <p className="text-gray-600 mt-2">Manage invitations to join the MIRA platform</p>
        </div>
        <button
          onClick={() => setShowInviteModal(true)}
          className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark flex items-center"
        >
          <FiSend className="mr-2" size={18} />
          Send Invitation
        </button>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Sent</p>
              <p className="text-2xl font-bold text-gray-900">{invitations.length}</p>
            </div>
            <FiMail className="text-gray-400" size={24} />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Pending</p>
              <p className="text-2xl font-bold text-yellow-600">
                {invitations.filter(inv => inv.status === 'pending').length}
              </p>
            </div>
            <FiClock className="text-yellow-400" size={24} />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Accepted</p>
              <p className="text-2xl font-bold text-green-600">
                {invitations.filter(inv => inv.status === 'accepted').length}
              </p>
            </div>
            <FiCheck className="text-green-400" size={24} />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Expired</p>
              <p className="text-2xl font-bold text-red-600">
                {invitations.filter(inv => inv.status === 'expired').length}
              </p>
            </div>
            <FiX className="text-red-400" size={24} />
          </div>
        </div>
      </div>

      {/* Invitations List */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b">
          <h3 className="text-lg font-semibold text-gray-900">All Invitations</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Company
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Subdomain
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Sent
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {invitations.map((invitation) => (
                <tr key={invitation.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{invitation.companyName}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm text-gray-900">{invitation.contactName}</div>
                      <div className="text-sm text-gray-500">{invitation.contactEmail}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {invitation.subdomain}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <StatusBadge status={invitation.status} />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(invitation.sentAt).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div className="flex items-center space-x-2">
                      {/* Show copy link for all pending invitations */}
                      {invitation.status === 'pending' && invitation.invitationUrl && (
                        <button
                          onClick={() => handleCopyLink(invitation.invitationUrl)}
                          className="p-1 hover:bg-gray-100 rounded"
                          title="Copy Invitation Link"
                        >
                          <FiCopy size={18} />
                        </button>
                      )}

                      {/* Show resend for pending or if email wasn't sent */}
                      {(invitation.status === 'pending' || !invitation.emailSent) && (
                        <button
                          onClick={() => handleResendInvitation(invitation.id)}
                          className="p-1 hover:bg-gray-100 rounded disabled:opacity-50"
                          title={!invitation.emailSent ? "Send Invitation Email" : "Resend Invitation Email"}
                        >
                          {resendingId === invitation.id ? (
                            <Loader variant="button" />
                          ) : (
                            <FiRefreshCw size={18} className={!invitation.emailSent ? "text-orange-600" : ""} />
                          )}
                        </button>
                      )}

                      {/* Show warning icon if email wasn't sent */}
                      {!invitation.emailSent && invitation.status === 'pending' && (
                        <span
                          className="text-orange-500"
                          title="Email not sent - click resend to try again"
                        >
                          ⚠️
                        </span>
                      )}

                      {/* Delete button */}
                      <button
                        onClick={() => handleDeleteInvitation(invitation.id)}
                        className="p-1 hover:bg-gray-100 rounded text-red-600"
                        title="Delete Invitation"
                      >
                        <FiTrash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Invite Modal */}
      {showInviteModal && <InviteModal />}
    </div>
  );
};

export default CompanyInvitations;
